import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { helpers, ToastContext, useFetch } from '@reveni-io/reveni-ui'

import AppContext from '@/common/context/app.context'
import { recoveryStatuses, statuses } from '@/returns/constants'
import { getTransactionStore } from '@/returns/helpers/helpers'
import useRestock from '@/returns/hooks/useRestock/useRestock'
import { amendSelectionStatuses } from '@/returns/pages/TransactionDetailPage/AmendRejectedDrawer/constants'
import returnsServices from '@/returns/services/services'

const useAmendRejected = ({ transaction, fetchData, fetchTimeline }) => {
  const { t } = useTranslation()
  const { info } = useContext(AppContext)
  const { setToastInfo } = useContext(ToastContext)
  const [showAmendRejectedDrawer, setShowAmendRejectedDrawer] = useState(false)
  const [itemSelections, setItemSelections] = useState({})
  const [notifyCustomer, setNotifyCustomer] = useState(true)

  const { restock, setRestock, restockLocation, setRestockLocation, inventoryLocations } = useRestock(
    getTransactionStore(info?.stores, transaction?.store_id),
    transaction?.permissions?.allows_manage_restock,
    info?.userInfo?.id
  )

  const { fetchData: amendRejected, loading: amendingRejected } = useFetch(returnsServices.amendRejected, {
    doInitialRequest: false,
  })

  const rejectedItems = helpers
    .joinLineItemsAndLineItemGroups(transaction?.line_items, transaction?.line_item_groups)
    .filter(item => item.status === statuses.rejected)

  const initializeSelections = () => {
    return rejectedItems.reduce((acc, item) => ({ ...acc, [item.id]: amendSelectionStatuses.rejected }), {})
  }

  const setItemSelection = (lineItemId, status) => {
    setItemSelections(prev => ({ ...prev, [lineItemId]: status }))
  }

  const getAmendedLineItems = () => {
    return Object.entries(itemSelections).filter(([, status]) => status === amendSelectionStatuses.amended)
  }

  const getAmendedLineItemIds = () => {
    const amendedIds = getAmendedLineItems().map(([id]) => id)

    return rejectedItems
      .filter(item => amendedIds.includes(item.id))
      .flatMap(item => (item.bundleItems?.length ? [item, ...item.bundleItems] : [item]))
      .map(item => helpers.getOriginalId(item.id))
  }

  const amendedCount = getAmendedLineItems().length

  const handleAmendRejected = async () => {
    try {
      await amendRejected({
        orderId: transaction?.id,
        payload: {
          restock,
          restock_location_id: restockLocation,
          line_item_ids: getAmendedLineItemIds(),
          notify_customer: notifyCustomer,
        },
      })

      setShowAmendRejectedDrawer(false)
      setToastInfo({ show: true, message: t('Productos corregidos correctamente'), type: 'success' })
      fetchData()
      fetchTimeline()
    } catch {
      setToastInfo({ show: true, message: t('Ocurrió un error al corregir los productos'), type: 'error' })
    }
  }

  const isAmendRejectedDisabled = () => {
    if (transaction?.is_instant) {
      return (
        ![statuses.rejected, statuses.partiallyAccepted].includes(transaction?.status) &&
        transaction?.refund_info?.recovery_status !== recoveryStatuses.notCollected
      )
    }

    return true
  }

  const handleChangeRestock = value => setRestock(value)

  const handleChangeLocation = e => setRestockLocation(e.target.value)

  const handleChangeNotifyCustomer = e => setNotifyCustomer(e.target.checked)

  const openAmendRejectedDrawer = () => {
    setItemSelections(initializeSelections())
    setNotifyCustomer(true)
    setShowAmendRejectedDrawer(true)
  }

  const closeAmendRejectedDrawer = () => {
    setShowAmendRejectedDrawer(false)
  }

  return {
    showAmendRejectedDrawer,
    openAmendRejectedDrawer,
    closeAmendRejectedDrawer,
    restock,
    restockLocation,
    amendingRejected,
    isAmendRejectedDisabled,
    rejectedItems,
    handleChangeRestock,
    handleChangeLocation,
    handleAmendRejected,
    locations: inventoryLocations,
    provider: info?.selectedStore?.provider,
    itemSelections,
    setItemSelection,
    amendedCount,
    notifyCustomer,
    handleChangeNotifyCustomer,
  }
}

export default useAmendRejected
